import React, { useEffect, useState } from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { Option, Select } from '@fiverr-private/forms';
import { Values } from '@fiverr-private/dropdown';
import { AppI18n, appTranslate } from '../../../../i18n';
import { EnrichedMetadataAnswerType } from '../../../../api';
import { LanguagesPairProps } from '../MetadataSection';
import { EditActions } from '../../EditActions';
import { isLanguagesPairValid } from './utils';

export const EditLanguagePairs = ({
  sectionContentState,
  setSectionContentState,
  onEditEnd,
  onCancel,
  shouldRemoveSection,
  section,
}: LanguagesPairProps) => {
  const [pair, setPair] = useState<{ from: string; to: string }>({
    from: sectionContentState.selectedAnswers[0],
    to: sectionContentState.selectedAnswers[1],
  });

  const [optionsFrom, setOptionsFrom] = useState<EnrichedMetadataAnswerType[]>([]);
  const [optionsTo, setOptionsTo] = useState<EnrichedMetadataAnswerType[]>([]);

  useEffect(() => {
    const pairToUpdate = [pair.from, pair.to];

    // update root section state only if the pair is valid
    if (isLanguagesPairValid(pairToUpdate)) {
      setSectionContentState(pairToUpdate);
    }

    // "From" option should not include the selected answer in the "To" dropdown and vice versa
    setOptionsTo(sectionContentState.allAnswers.filter((answer) => answer.slug !== pair.from));
    setOptionsFrom(sectionContentState.allAnswers.filter((answer) => answer.slug !== pair.to));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pair.from, pair.to]);

  const onChangeFrom = (values: Values) => {
    setPair({ from: values?.item as string, to: pair.to });
  };

  const onChangeTo = (values: Values) => {
    setPair({ from: pair.from, to: values?.item as string });
  };

  const isDisabled = () => {
    if (shouldRemoveSection) {
      return false;
    }

    if (!pair.from || !pair.to) {
      return true;
    }

    const [savedFrom, savedTo] = (section?.content || []) as any as string[];

    return pair.from === savedFrom || pair.to === savedTo;
  };

  return (
    <Stack direction="column">
      <Stack direction="row" gap="3" paddingY="4">
        <Stack direction="column" gap="1" width="100%">
          <Text>
            <AppI18n k="sections.language_pairs.translate_from" />
          </Text>
          <Select
            fullWidth
            value={{ item: pair.from }}
            placeholder={appTranslate('sections.language_pairs.placeholder').toString()}
            onChange={onChangeFrom}
          >
            {optionsFrom.map(({ slug, display_name }) => (
              <Option value={slug} key={slug}>
                {display_name}
              </Option>
            ))}
          </Select>
        </Stack>
        <Stack direction="column" gap="1" width="100%">
          <Text>
            <AppI18n k="sections.language_pairs.translate_to" />
          </Text>
          <Select
            fullWidth
            value={{ item: pair.to }}
            placeholder={appTranslate('sections.language_pairs.placeholder').toString()}
            onChange={onChangeTo}
          >
            {optionsTo.map(({ slug, display_name }) => (
              <Option value={slug} key={slug}>
                {display_name}
              </Option>
            ))}
          </Select>
        </Stack>
      </Stack>
      <EditActions
        onCancel={onCancel}
        onEditEnd={onEditEnd}
        isSaveDisabled={isDisabled()}
        shouldRemoveSection={shouldRemoveSection}
      />
    </Stack>
  );
};
